import type { MetricKind, MetricState, MetricValueResponse } from "./analyticsApi";

/**
 * The pure display mapping for a metric cell (tasks/cross-venue-analytics.md Tech decisions). It is the
 * ONE place the tri-state contract renders, so absence and add-on-off never collapse into a misleading
 * `0`:
 *  - `VALUE` → a formatted number (covers = an integer count; the three rates = a whole-percent), and a
 *    real `Value(0)` renders as `0` / `0%` (a present zero-activity period, edge case 3);
 *  - `NO_DATA` → the caller renders the localized "no data" marker (a gap, never a `0` — edge case 4);
 *  - `NOT_ENABLED` → the localized "not enabled" marker (WAITLIST add-on off — `0%` would misread, edge case 6).
 */
export interface FormattedMetric {
  state: MetricState;
  /** The formatted number for a `VALUE` (e.g. "42" or "25%"); empty for `NO_DATA`/`NOT_ENABLED`. */
  text: string;
}

/** True for the three ratio metrics (rendered as a percent); COVERS is a plain count. */
export function isRatioMetric(kind: MetricKind): boolean {
  return kind !== "COVERS";
}

export function formatMetric(kind: MetricKind, mv: MetricValueResponse): FormattedMetric {
  if (mv.state !== "VALUE" || mv.value === null) {
    return { state: mv.state, text: "" };
  }
  if (isRatioMetric(kind)) {
    // Fractions in [0, 1] → whole percent. `0` stays a real `0%`.
    return { state: "VALUE", text: `${Math.round(mv.value * 100)}%` };
  }
  return { state: "VALUE", text: `${Math.round(mv.value)}` };
}

/** A number in [0, 1] for a ratio metric VALUE (for sparkline scaling), else null. */
export function ratioOf(mv: MetricValueResponse): number | null {
  return mv.state === "VALUE" && mv.value !== null ? mv.value : null;
}
